import React, { memo } from 'react';
import { useSelector } from 'react-redux';
import { Empty } from 'antd';
import Column from '../../../shared/ui/Column';
import ActiveDisplay from '../../../shared/ui/ActiveDisplay';
import FetchDataWrapper from '../../../../../components/FetchDataWrapper';
import GridLayout from '../../../shared/ui/GridLayout';
import SelectDeskScheduleType from '../../../controllers/SelectDeskScheduleType';
import SelectDeskIntervalType from '../../../controllers/SelectDeskIntervalType';
import DeskOccupiedPeriods from '../../../controllers/DeskOccupiedPeriods';
import DeskUniqueRange from '../../../controllers/DeskUniqueRange';
import DeskDateRangeList from '../../../controllers/DeskDateRangeList';
import SelectDeskWorkingDays from '../../../controllers/SelectDeskWorkingDays';
import TakeDeskHoliday from '../../../controllers/TakeDeskHoliday';
import { useDeskSettingsDataQueries } from '../../../services/queries/deskSettingsData';
import { useDeskNamesQueryParams } from '../../../services/queries/deskNames';
import {
  useFieldArrayState,
  useFormWatch,
} from '../../../../../helpers/formManager';
import { fieldPathBuilders } from '../../../shared/configs/acceptanceForm';
import { selectors, sliceKeys } from '../../../model';
import Actions from './Actions';
import SelectDeskNameBlock from './SelectDeskNameBlock';
import SettingIntervals from './SettingIntervals';
import {
  useCreateDayIsImmutableFn,
  useDeskIsImmutable,
} from '../hooks/isImmutableFns';
import SwitchSettingsForAllDays from '../../../controllers/SwitchSettingsForAllDays';
import { TimeIntervalsProvider } from '../../../services/timeIntervals';
import { AllowClearClassicTimeProvider } from '../../../services/allowClearClassicTime';
import styles from './index.module.scss';

const { selectActiveIndex } = selectors[sliceKeys.desks];

const DeskSetupCol = ({ allowClearClassicTime }) => {
  const activeIndex = useSelector(selectActiveIndex);
  const hasContent = activeIndex !== undefined;

  const deskNamesParams = useDeskNamesQueryParams();
  const { data, isFetching, refetch } = useDeskSettingsDataQueries({
    deskNamesParams,
  });

  const shouldShowActions = hasContent && data !== 'Error';

  return (
    <Column action={shouldShowActions ? <Actions index={activeIndex} /> : null}>
      <FetchDataWrapper
        data={data}
        isFetching={isFetching}
        refetch={refetch}
        errMsg="Не удалось загрузить данные для настройки столов"
      >
        {hasContent ? (
          <TimeIntervalsProvider>
            <AllowClearClassicTimeProvider allowClear={allowClearClassicTime}>
              <Content activeIndex={activeIndex} />
            </AllowClearClassicTimeProvider>
          </TimeIntervalsProvider>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Выберите стол"
          />
        )}
      </FetchDataWrapper>
    </Column>
  );
};

export default DeskSetupCol;

const MemoizedFieldset = memo(Fieldset);
function Content({ activeIndex }) {
  const fields = useFieldArrayState();

  return fields.map((field, index) => (
    <ActiveDisplay key={field.key} isActive={activeIndex === index}>
      <MemoizedFieldset index={index} isActive={activeIndex === index} />
    </ActiveDisplay>
  ));
}

function Fieldset({ index, isActive }) {
  const deskDisabled = useDeskIsImmutable(index);
  const isDayDisabled = useCreateDayIsImmutableFn(index);

  return (
    <div className={styles.fieldset}>
      <SelectDeskNameBlock index={index} />
      <DeskOccupiedPeriods deskIndex={index} queryEnabled={isActive} />
      <DeskUniqueRange deskIndex={index} disabled={deskDisabled} />
      <DeskDateRangeList deskIndex={index} />
      <SelectDeskWorkingDays deskIndex={index} isDisabled={isDayDisabled} />
      <TakeDeskHoliday deskIndex={index} disabled={deskDisabled} />

      <ScheduleSettingsContainer
        deskIndex={index}
        deskDisabled={deskDisabled}
      />
    </div>
  );
}

const { workingDays: createWorkingDaysName } = fieldPathBuilders.desk;
const MemoizedScheduleSettings = memo(ScheduleSettings);
function ScheduleSettingsContainer({ deskIndex, deskDisabled }) {
  const workingDays = useFormWatch({
    name: createWorkingDaysName(deskIndex),
    defaultValue: [],
  });

  return (
    workingDays.length > 0 && (
      <MemoizedScheduleSettings
        deskIndex={deskIndex}
        deskDisabled={deskDisabled}
      />
    )
  );
}

function ScheduleSettings({ deskIndex, deskDisabled }) {
  return (
    <>
      <div>
        <h5 className={styles.title}>Настройка графика</h5>

        <GridLayout minWidth="200px" gap="20px" adaptive>
          <SelectDeskScheduleType
            deskIndex={deskIndex}
            disabled={deskDisabled}
          />
          <SelectDeskIntervalType
            deskIndex={deskIndex}
            disabled={deskDisabled}
          />
        </GridLayout>

        <SwitchSettingsForAllDays
          deskIndex={deskIndex}
          disabled={deskDisabled}
          size="small"
          className={styles.switchSettingsForAllDays}
        />
      </div>

      <SettingIntervals deskIndex={deskIndex} />
    </>
  );
}
